import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Tabs,
    Tab,
    Paper,
    CircularProgress,
    Alert
} from '@mui/material';
import { Chart } from 'react-google-charts';
import { useUser } from '../../contexts/UserContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    Query,
    DocumentData
} from 'firebase/firestore';
import { db } from '../../firebaseClient';

interface AnalyticsData {
    presentations: any[];
    evaluationResults: any[];
    users: any[];
}

const MyAnalytics: React.FC = () => {
    const { user, isTenantAdmin, isGlobalAdmin } = useUser();
    useDocumentTitle('My Analytics');

    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [data, setData] = useState<AnalyticsData>({
        presentations: [],
        evaluationResults: [],
        users: []
    });

    useEffect(() => {
        loadAnalyticsData();
    }, [user]);

    const loadAnalyticsData = async () => {
        if (!user) return;

        try {
            setLoading(true);
            setError('');

            // Load presentations, evaluation results, and users data
            const promises = [
                loadPresentations(),
                loadEvaluationResults(),
                loadUsers()
            ];

            const [presentations, evaluationResults, users] = await Promise.all(promises);

            setData({
                presentations,
                evaluationResults,
                users
            });
        } catch (err) {
            console.error('Error loading analytics data:', err);
            setError('Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    };

    const loadPresentations = (): Promise<any[]> => {
        return new Promise((resolve) => {
            let presentationsQuery: Query<DocumentData>;

            if (isGlobalAdmin()) {
                presentationsQuery = query(
                    collection(db, 'presentations'),
                    orderBy('createdAt', 'desc')
                );
            } else if (isTenantAdmin()) {
                presentationsQuery = query(
                    collection(db, 'presentations'),
                    where('tenantId', '==', user?.tenantId),
                    orderBy('createdAt', 'desc')
                );
            } else {
                presentationsQuery = query(
                    collection(db, 'presentations'),
                    where('userId', '==', user?.uid),
                    where('tenantId', '==', user?.tenantId),
                    orderBy('createdAt', 'desc')
                );
            }

            const unsubscribe = onSnapshot(presentationsQuery, (snapshot) => {
                const presentations: any[] = [];
                snapshot.forEach((doc) => {
                    presentations.push({ id: doc.id, ...doc.data() });
                });
                resolve(presentations);
                // We typically don't unsubscribe immediately if we want real-time, but specific logic here resolves once basically
                // For this pattern allowing strictly one-time fetch or keeping it, the original code looked one-offish in promise. 
                // We'll keep it as is, but really this should be a real-time hook.
                // But for converting existing logic:
                unsubscribe();
            }, (error) => {
                console.error('Error loading presentations:', error);
                resolve([]);
                // unsubscribe(); // redundant
            });
        });
    };

    const loadEvaluationResults = (): Promise<any[]> => {
        return new Promise((resolve) => {
            let evaluationQuery: Query<DocumentData>;

            if (isGlobalAdmin()) {
                evaluationQuery = query(
                    collection(db, 'evaluationResults'),
                    orderBy('evaluatedAt', 'desc')
                );
            } else if (isTenantAdmin()) {
                evaluationQuery = query(
                    collection(db, 'evaluationResults'),
                    where('tenantId', '==', user?.tenantId),
                    orderBy('evaluatedAt', 'desc')
                );
            } else {
                evaluationQuery = query(
                    collection(db, 'evaluationResults'),
                    where('userId', '==', user?.uid),
                    where('tenantId', '==', user?.tenantId),
                    orderBy('evaluatedAt', 'desc')
                );
            }

            const unsubscribe = onSnapshot(evaluationQuery, (snapshot) => {
                const evaluationResults: any[] = [];
                snapshot.forEach((doc) => {
                    evaluationResults.push({ id: doc.id, ...doc.data() });
                });
                resolve(evaluationResults);
                unsubscribe();
            }, (error) => {
                console.error('Error loading evaluation results:', error);
                resolve([]);
                unsubscribe();
            });
        });
    };

    const loadUsers = (): Promise<any[]> => {
        return new Promise((resolve) => {
            let usersQuery: Query<DocumentData>;

            if (isGlobalAdmin()) {
                usersQuery = query(collection(db, 'users'));
            } else if (isTenantAdmin()) {
                usersQuery = query(
                    collection(db, 'users'),
                    where('tenantId', '==', user?.tenantId)
                );
            } else {
                // Regular users only see themselves
                resolve([user]);
                return;
            }

            const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
                const users: any[] = [];
                snapshot.forEach((doc) => {
                    users.push({ id: doc.id, ...doc.data() });
                });
                resolve(users);
                unsubscribe();
            }, (error) => {
                console.error('Error loading users:', error);
                resolve([]);
                unsubscribe();
            });
        });
    };

    // Chart data generators
    const getEvaluationsByMonthData = () => {
        const monthlyData: Record<string, number> = {};

        data.evaluationResults.forEach(evaluation => {
            if (evaluation.evaluatedAt) {
                const date = new Date(evaluation.evaluatedAt.seconds * 1000);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
            }
        });

        const chartData: any[] = [['Month', 'Evaluations']];
        Object.entries(monthlyData)
            .sort(([a], [b]) => a.localeCompare(b))
            .forEach(([month, count]) => {
                chartData.push([month, count]);
            });

        return chartData.length > 1 ? chartData : [['Month', 'Evaluations'], ['No Data', 0]];
    };

    const getEvaluationsByUserData = () => {
        const userCounts: Record<string, number> = {};
        const userMap: Record<string, string> = {};

        // Create user map for names
        data.users.forEach(u => {
            userMap[u.id] = u.displayName || u.email || 'Unknown User';
        });

        data.evaluationResults.forEach(evaluation => {
            if (evaluation.userId) {
                const userName = userMap[evaluation.userId] || 'Unknown User';
                userCounts[userName] = (userCounts[userName] || 0) + 1;
            }
        });

        const chartData: any[] = [['User', 'Evaluations']];
        Object.entries(userCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 10) // Top 10 users
            .forEach(([user, count]) => {
                chartData.push([user, count]);
            });

        return chartData.length > 1 ? chartData : [['User', 'Evaluations'], ['No Data', 0]];
    };

    const getEvaluationsByLanguageData = () => {
        const languageCounts: Record<string, number> = {};

        data.presentations.forEach(presentation => {
            const language = presentation.language || 'Unknown';
            languageCounts[language] = (languageCounts[language] || 0) + 1;
        });

        const chartData: any[] = [['Language', 'Count']];
        Object.entries(languageCounts).forEach(([language, count]) => {
            chartData.push([language, count]);
        });

        return chartData.length > 1 ? chartData : [['Language', 'Count'], ['No Data', 0]];
    };

    const getCategoryTrendsByMonthData = () => {
        const monthlyCategories: Record<string, Record<string, number[]>> = {};

        data.evaluationResults.forEach(evaluation => {
            if (evaluation.evaluatedAt && evaluation.category) {
                const date = new Date(evaluation.evaluatedAt.seconds * 1000);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

                if (!monthlyCategories[monthKey]) {
                    monthlyCategories[monthKey] = {};
                }

                const category = evaluation.category;
                if (!monthlyCategories[monthKey][category]) {
                    monthlyCategories[monthKey][category] = [];
                }

                if (evaluation.overallScore) {
                    monthlyCategories[monthKey][category].push(evaluation.overallScore);
                }
            }
        });

        // Get all categories
        const categories = new Set<string>();
        Object.values(monthlyCategories).forEach(monthData => {
            Object.keys(monthData).forEach(category => categories.add(category));
        });

        const chartData: any[] = [['Month', ...Array.from(categories)]];
        Object.entries(monthlyCategories)
            .sort(([a], [b]) => a.localeCompare(b))
            .forEach(([month, categoryData]) => {
                const row: any[] = [month];
                categories.forEach(category => {
                    const scores = categoryData[category] || [];
                    const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
                    row.push(avgScore);
                });
                chartData.push(row);
            });

        return chartData.length > 1 ? chartData : [['Month', 'No Data'], ['No Data', 0]];
    };

    const getTopConsultantsByCategoryData = () => {
        const consultantsByCategory: Record<string, Record<string, number[]>> = {};
        const userMap: Record<string, string> = {};

        // Create user map
        data.users.forEach(u => {
            userMap[u.id] = u.displayName || u.email || 'Unknown User';
        });

        data.evaluationResults.forEach(evaluation => {
            if (evaluation.userId && evaluation.category && evaluation.overallScore) {
                const userName = userMap[evaluation.userId] || 'Unknown User';
                const category = evaluation.category;

                if (!consultantsByCategory[category]) {
                    consultantsByCategory[category] = {};
                }

                if (!consultantsByCategory[category][userName]) {
                    consultantsByCategory[category][userName] = [];
                }

                consultantsByCategory[category][userName].push(evaluation.overallScore);
            }
        });

        // Calculate averages and get top performers
        const topPerformers: [string, number][] = [];
        Object.entries(consultantsByCategory).forEach(([category, users]) => {
            Object.entries(users).forEach(([userName, scores]) => {
                const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
                topPerformers.push([`${userName} (${category})`, avgScore]);
            });
        });

        // Sort by score and take top 10
        topPerformers.sort(([, a], [, b]) => b - a);
        const chartData: any[] = [['Consultant (Category)', 'Average Score']];
        topPerformers.slice(0, 10).forEach(([name, score]) => {
            chartData.push([name, score]);
        });

        return chartData.length > 1 ? chartData : [['Consultant (Category)', 'Average Score'], ['No Data', 0]];
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            {/* Header */}
            <Typography variant="h4" gutterBottom>
                {isGlobalAdmin() ? 'Global Analytics' : isTenantAdmin() ? 'Tenant Analytics' : 'My Analytics'}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label="Evaluation Statistics" />
                    <Tab label="Evaluation Results Trends" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'space-between' }}>
                    {/* Evaluations by Month */}
                    <Box sx={{ flex: '1 1 300px', maxWidth: 'calc(33.33% - 16px)', minWidth: '300px' }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Evaluations by Month
                                </Typography>
                                <Box sx={{ height: 0, paddingBottom: '100%', position: 'relative' }}>
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                        <Chart
                                            chartType="ColumnChart"
                                            width="100%"
                                            height="100%"
                                            data={getEvaluationsByMonthData()}
                                            options={{
                                                title: 'Evaluations by Month',
                                                hAxis: { title: 'Month' },
                                                vAxis: { title: 'Number of Evaluations' },
                                                colors: ['#1976d2']
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Evaluations by User */}
                    <Box sx={{ flex: '1 1 300px', maxWidth: 'calc(33.33% - 16px)', minWidth: '300px' }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Evaluations by User
                                </Typography>
                                <Box sx={{ height: 0, paddingBottom: '100%', position: 'relative' }}>
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                        <Chart
                                            chartType="BarChart"
                                            width="100%"
                                            height="100%"
                                            data={getEvaluationsByUserData()}
                                            options={{
                                                title: 'Evaluations by User (Top 10)',
                                                hAxis: { title: 'Number of Evaluations' },
                                                vAxis: { title: 'Users' },
                                                colors: ['#388e3c']
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Evaluations by Language */}
                    <Box sx={{ flex: '1 1 300px', maxWidth: 'calc(33.33% - 16px)', minWidth: '300px' }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Evaluations by Language
                                </Typography>
                                <Box sx={{ height: 0, paddingBottom: '100%', position: 'relative' }}>
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                        <Chart
                                            chartType="PieChart"
                                            width="100%"
                                            height="100%"
                                            data={getEvaluationsByLanguageData()}
                                            options={{
                                                title: 'Distribution of Presentations by Language',
                                                pieHole: 0.4,
                                                colors: ['#f57c00', '#ff9800', '#ffb74d', '#ffcc02', '#ffd54f']
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            )}

            {activeTab === 1 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'space-between' }}>
                    {/* Category Trends by Month */}
                    <Box sx={{ flex: '1 1 400px', maxWidth: 'calc(50% - 16px)', minWidth: '400px' }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Category Trends by Month
                                </Typography>
                                <Box sx={{ height: 0, paddingBottom: '100%', position: 'relative' }}>
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                        <Chart
                                            chartType="LineChart"
                                            width="100%"
                                            height="100%"
                                            data={getCategoryTrendsByMonthData()}
                                            options={{
                                                title: 'Average Scores by Category Over Time',
                                                hAxis: { title: 'Month' },
                                                vAxis: { title: 'Average Score', minValue: 0, maxValue: 5 },
                                                curveType: 'function',
                                                legend: { position: 'bottom' },
                                                colors: ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#9c27b0']
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>

                    {/* Top Consultants by Category */}
                    <Box sx={{ flex: '1 1 400px', maxWidth: 'calc(50% - 16px)', minWidth: '400px' }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Top Consultants by Category
                                </Typography>
                                <Box sx={{ height: 0, paddingBottom: '100%', position: 'relative' }}>
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                                        <Chart
                                            chartType="BarChart"
                                            width="100%"
                                            height="100%"
                                            data={getTopConsultantsByCategoryData()}
                                            options={{
                                                title: 'Top Performing Consultants by Category',
                                                hAxis: { title: 'Average Score', minValue: 0, maxValue: 5 },
                                                vAxis: { title: 'Consultant (Category)' },
                                                colors: ['#673ab7'],
                                                chartArea: { left: 200, top: 50, width: '70%', height: '80%' }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default MyAnalytics;
